import gsap from 'gsap'

const MAGNIFYING_GLASS_RADIUS = 6
const MAGNIFYING_GLASS_DURATION = 1.5
const ELLIPSIS_STEP_DURATION = 0.2

export const animateMagnifyingGlass = (
  target: SVGGElement | null
): gsap.core.Tween | undefined => {
  if (!target) return undefined

  const state = { angle: 0 }

  return gsap.to(state, {
    angle: 360,
    duration: MAGNIFYING_GLASS_DURATION,
    repeat: -1,
    ease: 'none',
    onUpdate: () => {
      const radians = (state.angle * Math.PI) / 180
      gsap.set(target, {
        x: Math.cos(radians) * MAGNIFYING_GLASS_RADIUS,
        y: Math.sin(radians) * MAGNIFYING_GLASS_RADIUS,
      })
    },
  })
}

export const animateEllipsis = (
  target: HTMLElement | null
): gsap.core.Timeline | undefined => {
  if (!target) return undefined

  const dots = ['', '.', '..', '...', '..', '.']
  const timeline = gsap.timeline({ repeat: -1 })

  dots.forEach((dotString, index) => {
    timeline.set(
      target,
      { textContent: dotString },
      index * ELLIPSIS_STEP_DURATION
    )
  })

  return timeline
}

export const animateSparklesPopIn = (
  targets: (SVGGElement | null)[]
): gsap.core.Tween | undefined => {
  const validTargets = targets.filter((target): target is SVGGElement =>
    Boolean(target)
  )
  if (!validTargets.length) return undefined

  return gsap.fromTo(
    validTargets,
    { scale: 0, opacity: 0 },
    {
      scale: 1,
      opacity: 1,
      duration: 0.75,
      stagger: 0.225,
      ease: 'back.out(2)',
      transformOrigin: 'center',
    }
  )
}

export const animateFoundSteps = (
  container: HTMLElement | null
): gsap.core.Tween | undefined => {
  if (!container?.children.length) return undefined

  return gsap.fromTo(
    container.children,
    { opacity: 0, y: 12 },
    {
      opacity: 1,
      y: 0,
      duration: 0.4,
      stagger: 0.15,
      ease: 'power2.out',
    }
  )
}

/**
 * Counts up to whatever has actually been settled. `from` lets a later call resume where the
 * last one stopped, so arriving data extends the count instead of restarting it, which is
 * what makes the duration track real work rather than a fixed six seconds.
 */
export const animateTransactionCounter = (
  onUpdate: (value: number) => void,
  target: number,
  from = 0,
  duration = 6,
  onComplete?: () => void
): gsap.core.Tween => {
  const state = { value: from }

  return gsap.to(state, {
    value: target,
    duration,
    ease: 'sine.inOut',
    onUpdate: () => {
      onUpdate(Math.round(state.value))
    },
    onComplete,
  })
}
